import { useState, useEffect, useCallback, useMemo } from 'react';
import { getApiUrl } from '../config';
import { Match, Player } from '../types';

// Interfaz para auto-reportes
interface SelfReport {
  goals: number;
  assists: number;
  saves: number;
  isSubmitted: boolean;
  validationStatus: 'PENDING' | 'APPROVED' | 'REJECTED';
  rejectionReason?: string | null;
  validatorId?: string | null;
  validatedAt?: string | null;
  validator?: { name: string };
}

interface PendingValidation {
  reporterId: string;
  reporterName: string;
  stats: {
    goals: number;
    assists: number;
    saves: number;
  };
}

interface SelfReportsState {
  selfReports: { [playerId: string]: SelfReport };
  pendingValidation: PendingValidation | null;
  submittingSelfReport: boolean;
  validatingReport: boolean;
}

interface SelfReportsActions {
  fetchSelfReports: () => Promise<void>;
  submitSelfReport: (stats: { goals: number; assists: number; saves: number }) => Promise<void>;
  validateReport: (approved: boolean, rejectionReason?: string) => Promise<void>;
  handleValidateStats: (player: Player) => void;
  setPendingValidation: (validation: PendingValidation | null) => void;
  
  // Computed values
  canSubmitSelfReport: boolean;
  hasPendingValidation: boolean;
  hasRejectedReport: boolean;
  hasValidatedReport: boolean;
  getSelfReportStats: { goals: number; assists: number; saves: number };
  getValidatorName: string;
  getRejectionInfo: { reason: string; rejectedBy: string } | null;
  hasPlayerPendingStats: (userId: string) => boolean;
}

interface UseSelfReportsProps {
  matchId: string | undefined;
  match: Match | null;
  user: any;
  onShowNotification: (message: string, type: 'success' | 'error') => void;
  onFetchMatch: () => Promise<void>;
  onSetShowValidationModal: (show: boolean) => void;
}

export const useSelfReports = ({
  matchId,
  match,
  user,
  onShowNotification,
  onFetchMatch,
  onSetShowValidationModal
}: UseSelfReportsProps): SelfReportsState & SelfReportsActions => {
  const [selfReports, setSelfReports] = useState<{ [playerId: string]: SelfReport }>({});
  const [pendingValidation, setPendingValidation] = useState<PendingValidation | null>(null);
  const [submittingSelfReport, setSubmittingSelfReport] = useState(false);
  const [validatingReport, setValidatingReport] = useState(false);

  // API call with authentication
  const apiCallWithAuth = useCallback(async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('authToken');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers
    };

    return fetch(url, { 
      ...options, 
      headers
    });
  }, []);

  // Fetch self reports
  const fetchSelfReports = useCallback(async () => {
    if (!matchId) return;
    
    try {
      const response = await fetch(getApiUrl(`/api/matches/${matchId}/self-reports`));
      if (response.ok) {
        const data = await response.json();
        const reportsMap: any = {};
        data.selfReports.forEach((report: any) => {
          reportsMap[report.playerId] = report;
        });
        setSelfReports(reportsMap);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching self reports:', error);
    }
  }, [matchId]);

  // Submit self report
  const submitSelfReport = useCallback(async (stats: { goals: number; assists: number; saves: number }) => {
    if (!match || !user) return;
    
    setSubmittingSelfReport(true);
    try {
      const response = await apiCallWithAuth(getApiUrl(`/api/matches/${matchId}/self-report`), {
        method: 'POST',
        body: JSON.stringify(stats)
      });

      if (response.ok) {
        onShowNotification('Reporte enviado exitosamente. Esperando validación.', 'success');
        await fetchSelfReports();
      } else {
        const error = await response.json();
        onShowNotification(error.error || 'Error enviando reporte', 'error');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error submitting self report:', error);
      onShowNotification('Error enviando reporte', 'error');
    } finally {
      setSubmittingSelfReport(false);
    }
  }, [match, user, matchId, apiCallWithAuth, onShowNotification, fetchSelfReports]);

  // Validate report
  const validateReport = useCallback(async (approved: boolean, rejectionReason?: string) => {
    if (!pendingValidation || !matchId) return;
    
    setValidatingReport(true);
    try {
      const response = await apiCallWithAuth(getApiUrl(`/api/matches/${matchId}/validate-report`), {
        method: 'POST',
        body: JSON.stringify({
          reporterId: pendingValidation.reporterId,
          approved,
          ...(rejectionReason && { rejectionReason })
        })
      });

      if (response.ok) {
        const message = approved ? 'Reporte aprobado exitosamente' : 'Reporte rechazado';
        onShowNotification(message, 'success');
        setPendingValidation(null);
        onSetShowValidationModal(false);
        await fetchSelfReports();
      } else {
        const error = await response.json();
        onShowNotification(error.error || 'Error validando reporte', 'error');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error validating report:', error);
      onShowNotification('Error validando reporte', 'error');
    } finally {
      setValidatingReport(false);
    }
  }, [pendingValidation, matchId, apiCallWithAuth, onShowNotification, fetchSelfReports, onSetShowValidationModal]);

  // Validar stats de otro jugador
  const handleValidateStats = useCallback((player: Player) => {
    const selfReport = selfReports[player.id];
    if (!selfReport) return;
    
    setPendingValidation({
      reporterId: player.userId || '',
      reporterName: player.name,
      stats: {
        goals: selfReport.goals,
        assists: selfReport.assists,
        saves: selfReport.saves
      }
    });
    
    onSetShowValidationModal(true);
  }, [selfReports, onSetShowValidationModal]);

  // Load self reports on mount
  useEffect(() => {
    fetchSelfReports();
  }, [fetchSelfReports]);

  // Helper: defensive accessor — algunos eventos WebSocket pueden setear `match`
  // sin el campo `players` y eso crasheaba toda la app.
  const playersOf = (m: any): any[] => (Array.isArray(m?.players) ? m.players : []);

  // Computed values
  const canSubmitSelfReport = useMemo(() => {
    if (!match || !user || match.status !== 'FINISHED') return false;

    const player = playersOf(match).find(p => p.userId === user.id);
    if (!player || !player.userId) return false;

    const selfReport = selfReports[player.id];
    return !selfReport || !selfReport.isSubmitted || selfReport.validationStatus === 'REJECTED';
  }, [match, user, selfReports]);

  const hasPendingValidation = useMemo(() => {
    if (!match || !user || match.status !== 'FINISHED') return false;

    const player = playersOf(match).find(p => p.userId === user.id);
    if (!player) return false;

    const selfReport = selfReports[player.id];
    return selfReport && selfReport.isSubmitted && selfReport.validationStatus === 'PENDING';
  }, [match, user, selfReports]);

  const hasRejectedReport = useMemo(() => {
    if (!match || !user || match.status !== 'FINISHED') return false;

    const player = playersOf(match).find(p => p.userId === user.id);
    if (!player) return false;

    const selfReport = selfReports[player.id];
    return selfReport && selfReport.validationStatus === 'REJECTED';
  }, [match, user, selfReports]);

  const hasValidatedReport = useMemo(() => {
    if (!match || !user || match.status !== 'FINISHED') return false;

    const player = playersOf(match).find(p => p.userId === user.id);
    if (!player) return false;

    const selfReport = selfReports[player.id];
    return selfReport && selfReport.isSubmitted && selfReport.validationStatus === 'APPROVED';
  }, [match, user, selfReports]);

  const getSelfReportStats = useMemo(() => {
    if (!match || !user) return { goals: 0, assists: 0, saves: 0 };

    const player = playersOf(match).find(p => p.userId === user.id);
    if (!player) return { goals: 0, assists: 0, saves: 0 };
    
    const selfReport = selfReports[player.id];
    if (!selfReport) return { goals: 0, assists: 0, saves: 0 };
    
    return {
      goals: selfReport.goals || 0,
      assists: selfReport.assists || 0,
      saves: selfReport.saves || 0
    };
  }, [match, user, selfReports]);

  const getValidatorName = useMemo(() => {
    if (!match || !user) return '';

    const player = playersOf(match).find(p => p.userId === user.id);
    if (!player) return '';

    const selfReport = selfReports[player.id];
    if (!selfReport || !selfReport.validator) return '';

    return selfReport.validator.name || '';
  }, [match, user, selfReports]);

  const getRejectionInfo = useMemo(() => {
    if (!match || !user) return null;

    const player = playersOf(match).find(p => p.userId === user.id);
    if (!player) return null;

    const selfReport = selfReports[player.id];
    if (!selfReport || selfReport.validationStatus !== 'REJECTED') return null;

    return {
      reason: selfReport.rejectionReason || 'Sin motivo especificado',
      rejectedBy: selfReport.validator?.name || 'Usuario'
    };
  }, [match, user, selfReports]);

  // Función para detectar si un jugador específico tiene stats pendientes de validación
  const hasPlayerPendingStats = useCallback((userId: string) => {
    if (!match || match.status !== 'FINISHED') return false;

    const player = playersOf(match).find(p => p.userId === userId);
    if (!player || !player.userId) return false;
    
    const selfReport = selfReports[player.id];
    return selfReport && selfReport.isSubmitted && selfReport.validationStatus === 'PENDING';
  }, [match, selfReports]);

  return {
    // State
    selfReports,
    pendingValidation,
    submittingSelfReport,
    validatingReport,
    
    // Actions
    fetchSelfReports,
    submitSelfReport,
    validateReport,
    handleValidateStats,
    setPendingValidation,
    
    // Computed values
    canSubmitSelfReport,
    hasPendingValidation,
    hasRejectedReport,
    hasValidatedReport,
    getSelfReportStats,
    getValidatorName,
    getRejectionInfo,
    hasPlayerPendingStats
  };
};
